<template>
  <AppDialog :is-open="isDialogOpened"
             size="xl"
             :persistent="true"
             :ok-text="$t('common.download')"
             :ok-disabled="!canDownload"
             :ok-loading="archivesDownloadPending"
             @ok="downloadArchives"
             @cancel="closeDialog"
  >
    <template #title>
      <span>{{ $t('project.documents.dialogs.DocumentsDownloadFilesDialog.dialogTitle') }}</span>
    </template>
    <template #body>
      <template v-if="hasAlerts">
        <app-alert-in-page class="mb-6" :dismissible="false">
          <i18n-t keypath="project.documents.dialogs.DocumentsDownloadFilesDialog.caption"
                  tag="span"
                  scope="global"
          >
            <template #fileSize>
              <app-filesize-display :value="SIZE_LIMIT"/>
            </template>
          </i18n-t>
        </app-alert-in-page>
      </template>

      <p class="text--small text--bold">
        {{ $t('project.documents.dialogs.DocumentsDownloadFilesDialog.yourSelection') }}
      </p>

      <v-list class="listContent">
        <template v-for="(document, i) in selectedDocuments" :key="`doc-${i}`">
          <v-list-item class="px-0 py-4">
            <template #prepend>
              <ClosdFildersIcon :document="document"/>
            </template>

            <div class="d-flex justify-space-between align-center ml-4">
              <span>{{ document.docName }}</span>

              <div>
                <app-filesize-display :value="document.docSize"
                                      :default-value="0"
                                      class="mr-2"
                />

                <AppButton icon
                           variant="text"
                           density="comfortable"
                           @click="onRemoveDocument(i)"
                >
                  <app-material-icon name="delete" color="inherit" />
                </AppButton>
              </div>
            </div>
          </v-list-item>
          <v-divider
            v-if="i !== selectedDocuments.length"
            :key="`divider-${i}`"
          ></v-divider>
        </template>
      </v-list>

      <div class="my-4 d-flex justify-space-between">
        <p class="text--small text--bold"
                  v-t="{
                      path: 'project.documents.dialogs.DocumentsDownloadFilesDialog.selectedItems',
                      plural: selectedDocuments.length,
                      args: { selectedItems: selectedDocuments.length },
                  }"
        />
        <div>
          <span class="text--small">
            {{ $t('project.documents.dialogs.DocumentsDownloadFilesDialog.selectionSize') }}
          </span>

          <span class="ml-2 text--small text--bold">
            <app-filesize-display :value="selectedDocumentsSize" :default-value="0" />
          </span>
        </div>
      </div>
      <AppDownloadFile ref="appDownloadFileRef" :link="downloadLink" />
    </template>
  </AppDialog>
</template>

<script>
import axios from 'axios'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { dialogMixin } from '@/mixins/dialogMixin'
import { useArchivesStore } from '@/stores/archivesStore'

/**
 * Size limit in byte
 * @type {number}
 */
const SIZE_LIMIT = 209715200

export default {
  name: 'ArchivesDownloadDialog',
  components: {
    AppButton,
    AppDialog,
    AppDownloadFile,
    ClosdFildersIcon,
  },
  mixins: [dialogMixin],
  setup() {
    const archivesStore = useArchivesStore()
    return { archivesStore }
  },
  data () {
    return {
      downloadLink: '',
      isBibleGenerateDialogOpened: false,
      SIZE_LIMIT,
    }
  },
  computed: {
    selectedDocumentsSize() {
      return this.archivesStore.selectedDocumentsSize
    },
    selectedDocuments() {
      return this.archivesStore.selectedDocuments
    },
    archivesDownloadPending() {
      return this.archivesStore.archivesDownloadPending
    },
    canDownload () {
      return this.selectedDocuments.length !== 0 && this.selectedDocumentsSize < SIZE_LIMIT
    },
    hasAlerts () {
      return this.selectedDocumentsSize > SIZE_LIMIT
    },
    selectedDocumentsFileIds () {
      return this.selectedDocuments.map(doc => doc.id)
    },
  },
  methods: {
    async downloadArchives () {
      this.downloadLink = `${axios.defaults.baseURL}/downloads/archives?archiveIds=${this.selectedDocumentsFileIds.join(',')}`
      await this.$nextTick()
      this.$refs.appDownloadFileRef.submitForm()
      this.closeDialog()
    },
    onRemoveDocument (index) {
      this.archivesStore.removeDocument(index)
      if (this.selectedDocuments.length === 0) {
        this.closeDialog()
      }
    },
  },
}
</script>

<style scoped lang="scss">
.listContent {
  max-height: 300px;
  overflow-y: auto;
}
</style>
